import React from "react";
import { Badge } from "@/components/ui/badge";
import { Truck, CheckCircle2, AlertTriangle, Package, RotateCw, Ban, Clock } from "lucide-react";
import { api } from "@/lib/api";
import { useEffect, useRef } from "react";

// Kleur + icoon per pakket-statuscategorie zoals bepaald door de backend
// (SHIPMENT_STATUS_MAP in myparcel_service.py).
const CATEGORIE_STYLES = {
  concept:           { cls: "bg-secondary text-muted-foreground", Icon: Clock,         label: "Concept" },
  registered:        { cls: "bg-slate-100 text-slate-700",         Icon: Package,       label: "Aangemeld" },
  printed:           { cls: "bg-blue-100 text-blue-800",           Icon: Package,       label: "Label geprint" },
  handed_to_carrier: { cls: "bg-blue-100 text-blue-800",           Icon: Truck,         label: "Bij vervoerder" },
  in_transit:        { cls: "bg-indigo-100 text-indigo-800",       Icon: Truck,         label: "Onderweg" },
  out_for_delivery:  { cls: "bg-amber-100 text-amber-800",         Icon: Truck,         label: "Uit voor bezorging" },
  delivered:         { cls: "bg-emerald-100 text-emerald-800",     Icon: CheckCircle2,  label: "Bezorgd" },
  undeliverable:     { cls: "bg-rose-100 text-rose-800",           Icon: AlertTriangle, label: "Bezorging mislukt" },
  returned:          { cls: "bg-orange-100 text-orange-800",       Icon: RotateCw,      label: "Retour" },
  cancelled:         { cls: "bg-neutral-200 text-neutral-700",     Icon: Ban,           label: "Geannuleerd" },
  onbekend:          { cls: "bg-secondary text-muted-foreground",  Icon: Clock,         label: "Onbekend" },
};

/**
 * Kleine, uniforme status-badge voor MyParcel-zendingen. Wordt op meerdere
 * plekken gebruikt (Pakbonnen-lijst, VerkoopkansDetail, VerkoopfactuurDetail)
 * zodat de status er overal hetzelfde uitziet.
 */
export function PakketStatusBadge({ status, omschrijving, tracktraceUrl, size = "sm" }) {
  if (!status) return null;
  const style = CATEGORIE_STYLES[status] || CATEGORIE_STYLES.onbekend;
  const Icon = style.Icon;
  const badge = (
    <Badge className={`gap-1 border-transparent ${style.cls} ${size === "xs" ? "text-[10px] px-1.5 py-0" : ""}`}
           data-testid={`pakket-status-${status}`}>
      <Icon className="h-3 w-3" /> {omschrijving || style.label}
    </Badge>
  );
  if (tracktraceUrl) {
    return (
      <a href={tracktraceUrl} target="_blank" rel="noreferrer" className="inline-block hover:opacity-90"
         title="Open track & trace" onClick={(e)=>e.stopPropagation()}>{badge}</a>
    );
  }
  return badge;
}

/**
 * Hook die één keer per component-mount (via een module-scope guard) een
 * pakket-status-sync triggert. De backend heeft zelf een 5-min throttle, dus
 * meerdere frontends of tab-switches kunnen dit zonder risico op te veel
 * MyParcel-calls aanroepen.
 */
let _laatstGeprobeerd = 0;
const CLIENT_THROTTLE_MS = 60 * 1000; // client-side extra throttle van 1 min

export function usePakketSync(onDone) {
  const cbRef = useRef(onDone);
  cbRef.current = onDone;
  useEffect(() => {
    const now = Date.now();
    if (now - _laatstGeprobeerd < CLIENT_THROTTLE_MS) return;
    _laatstGeprobeerd = now;
    api.post("/pakbonnen/sync-pakket-statussen", {})
      .then((res) => { if (!res.data.throttled && res.data.gewijzigd > 0) cbRef.current?.(res.data); })
      .catch(() => {});
  }, []);
}
